import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import root from "../assets/styles/Root.module.css";
import bcss from "../assets/styles/Battles.module.css";

export default function BattleRoomPage() {
  const { id } = useParams();
  const { authFetch, user, loadProfile } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [b, setB] = useState(null);
  const [error, setError] = useState("");
  const [caseDetail, setCaseDetail] = useState(null);
  const prevStatusRef = useRef(null);

  const participants = useMemo(() => Array.isArray(b?.participants) ? b.participants : [], [b]);
  const teamA = useMemo(() => participants.filter(p => p.team === "A").sort((x,y)=>x.slot-y.slot), [participants]);
  const teamB = useMemo(() => participants.filter(p => p.team === "B").sort((x,y)=>x.slot-y.slot), [participants]);
  const isHost = useMemo(() => !!(b && user && b.host?.id === user.id), [b, user]);
  const userInBattle = useMemo(() => !!participants.find(p => p.user?.id === user?.id), [participants, user]);
  const requiredPlayers = useMemo(() => b?.mode === "2v2" ? 4 : 2, [b?.mode]);
  const canJoin = useMemo(() => {
    if (!b || !user) return false;
    if (b.status !== "waiting") return false;
    if (userInBattle) return false;
    return participants.length < requiredPlayers;
  }, [b, user, userInBattle, participants.length, requiredPlayers]);
  const canStart = useMemo(() => b && b.status === "waiting" && participants.length >= requiredPlayers && isHost, [b, participants.length, requiredPlayers, isHost]);
  const canOpen = useMemo(() => b && b.status === "active" && b.current_round < b.rounds, [b]);
  const finished = useMemo(() => b && b.status === "finished", [b]);

  // Итоги боя для отображения
  const { sumA, sumB, pool, winnerTeam, winnerShare } = useMemo(() => {
    const rows = Array.isArray(b?.rounds_data) ? b.rounds_data : [];
    let a = 0, c = 0;
    for (const r of rows) {
      if (b?.mode === "2v2") {
        a += Number(r?.teamA?.[0] || 0) + Number(r?.teamA?.[1] || 0);
        c += Number(r?.teamB?.[0] || 0) + Number(r?.teamB?.[1] || 0);
      } else {
        a += Number(r?.hostAmount || 0);
        c += Number(r?.oppAmount || r?.opponentAmount || 0);
      }
    }
    const total = a + c;
    let team = null;
    if (a > c) team = "A";
    else if (c > a) team = "B";
    let winners = 0;
    if (team) {
      winners = participants.filter(p => p.team === team).length || 1;
    }
    const share = team ? (total / Math.max(1, winners)) : 0;
    return { sumA: a, sumB: c, pool: total, winnerTeam: team, winnerShare: share };
  }, [b?.rounds_data, b?.mode, participants]);

  // Данные для полосы кейсов
  const roundsList = useMemo(() => Array.from({ length: b?.rounds || 0 }, (_, i) => i + 1), [b?.rounds]);
  const activeRound = useMemo(() => {
    if (!b?.rounds) return 0;
    if (b.status === "finished") return b.rounds;
    const next = (b.current_round || 0) + 1;
    return Math.min(next, b.rounds);
  }, [b?.rounds, b?.current_round, b?.status]);

  const itemsRef = useMemo(() => [], []);
  const stripRef = useRef(null);
  useEffect(() => {
    const el = itemsRef[activeRound - 1];
    const strip = stripRef.current;
    if (el && strip) {
      const itemCenter = el.offsetLeft + el.offsetWidth / 2;
      const targetLeft = Math.max(0, itemCenter - strip.clientWidth / 2);
      strip.scrollTo({ left: targetLeft, behavior: "smooth" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRound, b?.id]);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await authFetch(`/api/battles/${id}/`);
      if (!res.ok) throw new Error("not found");
      setB(await res.json());
    } catch (e) {
      setError(e?.message || "Ошибка");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
  useEffect(() => {
    (async () => {
      if (!b?.case_id) return;
      try {
        const res = await authFetch(`/api/cases/${b.case_id}/`);
        if (res.ok) setCaseDetail(await res.json());
      } catch {}
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [b?.case_id]);

  // Следим за сменой статуса батла: waiting -> active (списание), active -> finished (выплата)
  useEffect(() => {
    const prev = prevStatusRef.current;
    const curr = b?.status;
    if (prev && curr && prev !== curr) {
      if ((prev === "waiting" && curr === "active") || (prev === "active" && curr === "finished")) {
        // Обновим баланс пользователя в шапке
        loadProfile?.();
      }
    }
    prevStatusRef.current = curr || null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [b?.status]);

  // Авто-обновление состояния батла (псевдо-реалтайм)
  useEffect(() => {
    if (!b) return;
    if (b.status !== "waiting" && b.status !== "active") return;
    let timer = null;
    let stopped = false;
    const tick = async () => {
      if (stopped) return;
      try {
        const res = await authFetch(`/api/battles/${id}/`);
        if (res.ok) {
          const j = await res.json();
          setB(j);
          // останавливаем, если завершён
          if (j.status === "finished") {
            stopped = true;
            if (timer) clearInterval(timer);
          }
        }
      } catch {}
    };
    // быстрый первый тик и затем интервал
    tick();
    timer = setInterval(tick, 1500);
    const onVis = () => {
      if (document.visibilityState === "visible") tick();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      stopped = true;
      if (timer) clearInterval(timer);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [b?.status, id, authFetch]); 

  const onJoin = async () => {
    setSaving(true);
    
    try {
      const res = await authFetch(`/api/battles/${id}/join/`, { method: "POST" });
      
      if (res.ok) setB(await res.json());
    } catch (e) {
      setError(e?.message || "Ошибка");
      console.log(e);
    } finally {
      console.log("finally");
      setSaving(false);
    }
  };

  const onStart = async () => {
    setSaving(true);
    try {
      const res = await authFetch(`/api/battles/${id}/start/`, { method: "POST" });
      if (res.ok) {
        setB(await res.json());
        // мгновенно подхватим изменение баланса после списания
        loadProfile?.();
      }
    } catch (e) {
      setError(e?.message || "Ошибка");
    } finally {
      setSaving(false);
    }
  };

  const onOpen = async () => {
    setSaving(true);
    try {
      const res = await authFetch(`/api/battles/${id}/open-next/`, { method: "POST" });
      if (res.ok) {
        const j = await res.json();
        setB(j);
        if (j?.status === "finished") {
          // обновим баланс после выплаты
          loadProfile?.();
        }
      }
    } catch (e) {
      setError(e?.message || "Ошибка");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <h2>Батл #{id}</h2>
        <button className={root.btn} onClick={() => navigate("/battles")}>К списку</button>
      </div>

      {error && <div style={{ color: "red", marginBottom: 12 }}>{error}</div>}

      {loading ? (
        <div className={root.card}>Загрузка…</div>
      ) : !b ? (
        <div className={root.card}>Батл не найден</div>
      ) : (
        <>
          {/* Top bar with rounds and case thumbs */}
          <div className={root.card} style={{ marginBottom: 12 }}>
            <div className={bcss.bar}>
              <div className={bcss.barLeft}>
                ROUNDS {b.current_round} OF {b.rounds}
              </div>
              <div className={bcss.barCenter}>
                <div className={bcss.caseStrip} ref={stripRef}>
                  {roundsList.map((n, idx) => (
                    <div
                      key={n}
                      className={`${bcss.caseItem} ${activeRound === n ? bcss.caseItemActive : ""}`}
                      ref={el => itemsRef[idx] = el}
                    >
                      <div className={`${bcss.caseThumb} ${activeRound === n ? bcss.caseThumbActive : ""}`}>
                        {caseDetail?.avatar_url
                          ? <img src={caseDetail.avatar_url} alt={caseDetail?.name || "case"} />
                          : <div style={{width:"100%",height:"100%",display:"flex",alignItems:"center",justifyContent:"center",color:"#94a3b8"}}>CASE</div>
                        }
                      </div>
                      <div className={bcss.casePriceSmall}>
                        ${Number(b.case_price_usd || caseDetail?.price_usd || 0).toFixed(2)}
                      </div>
                      <div className={bcss.caseRound}>#{n}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div className={bcss.barRight}>
                TOTAL COST ${Number(
                  (b.total_cost_usd != null ? b.total_cost_usd : (b.rounds * (b.case_price_usd || caseDetail?.price_usd || 0)))
                ).toFixed(2)}
              </div>
            </div>
          </div>

          {/* VS Band with totals */}
          <div className={bcss.vsBand}>
            <div className={bcss.teamLabel}>
              TEAM 1 • <span className={bcss.teamSum}>${Number(b.mode === "2v2" ? (b.teamA_total || 0) : (b.host_total || 0)).toFixed(2)}</span>
            </div>
            <div className={bcss.vs}>VS</div>
            <div className={bcss.teamLabel} style={{ justifyContent: "flex-end" }}>
              <span className={`${bcss.teamSum} ${bcss.teamSumR}`}>${Number(b.mode === "2v2" ? (b.teamB_total || 0) : (b.opponent_total || 0)).toFixed(2)}</span> • TEAM 2
            </div>
          </div>

          {/* Actions */}
          <div style={{ marginBottom: 12, display: "flex", gap: 8 }}>
            {canJoin && <button className={root.btnPrimary} disabled={saving} onClick={onJoin}>Войти в комнату</button>}
            {canStart && <button className={root.btnPrimary} disabled={saving} onClick={onStart}>Старт</button>}
            {canOpen && <button className={root.btnPrimary} disabled={saving} onClick={onOpen}>Открыть следующий</button>}
          </div>

          {/* Players grid */}
          <div className={`${bcss.grid} ${b.mode === "2v2" ? bcss.grid4 : bcss.grid2}`}>
            {/* Left team */}
            <div className={bcss.card}>
              <div className={bcss.reel} />
              <div className={bcss.user}>
                {(teamA[0]?.name || teamA[0]?.user?.username || teamA[0]?.user?.email) ??
                  (b.host?.username || b.host?.email || (b.host ? `user#${b.host.id}` : "—"))}
              </div>
              <div className={bcss.roundsList}>
                {(b.rounds_data || []).map(r => (
                  <span key={r.round} className={bcss.roundPill}>
                    R{r.round}: ${Number(b.mode === "2v2" ? (r.teamA?.[0] || 0) : (r.hostAmount || 0)).toFixed(2)}
                  </span>
                ))}
              </div>
            </div>
            {b.mode === "2v2" && (
              <div className={bcss.card}>
                <div className={bcss.reel} />
                <div className={bcss.user}>{teamA[1]?.name || teamA[1]?.user?.username || teamA[1]?.user?.email || "—"}</div>
                <div className={bcss.roundsList}>
                  {(b.rounds_data || []).map(r => (
                    <span key={r.round} className={bcss.roundPill}>
                      R{r.round}: ${Number(r.teamA?.[1] || 0).toFixed(2)}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Right team */}
            <div className={bcss.card}>
              <div className={bcss.reel} />
              <div className={bcss.user}>
                {(teamB[0]?.name || teamB[0]?.user?.username || teamB[0]?.user?.email) ??
                  (b.opponent ? (b.opponent.username || b.opponent.email || `user#${b.opponent?.id}`) : "—")}
              </div>
              <div className={bcss.roundsList}>
                {(b.rounds_data || []).map(r => (
                  <span key={r.round} className={bcss.roundPill}>
                    R{r.round}: ${Number(b.mode === "2v2" ? (r.teamB?.[0] || 0) : (r.oppAmount || r.opponentAmount || 0)).toFixed(2)}
                  </span>
                ))}
              </div>
            </div>
            {b.mode === "2v2" && (
              <div className={bcss.card}>
                <div className={bcss.reel} />
                <div className={bcss.user}>{teamB[1]?.name || teamB[1]?.user?.username || teamB[1]?.user?.email || "—"}</div>
                <div className={bcss.roundsList}>
                  {(b.rounds_data || []).map(r => (
                    <span key={r.round} className={bcss.roundPill}>
                      R{r.round}: ${Number(r.teamB?.[1] || 0).toFixed(2)}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {finished && (
            <div className={root.card} style={{ marginTop: 12 }}>
              <h3 style={{ marginTop: 0, marginBottom: 12 }}>Результат</h3>
              {!b.winner ? (
                <div>Ничья</div>
              ) : (
                <div>
                  Победитель: {b.winner.username || b.winner.email || `user#${b.winner.id}`}
                  <div style={{ marginTop: 6, color: "#0f172a" }}>
                    Суммарный выигрыш победителя: <b>${Number(winnerShare || 0).toFixed(2)}</b>
                    <span style={{ marginLeft: 8, color: "#64748b" }}>(общий банк: ${Number(pool || 0).toFixed(2)})</span>
                  </div>
                </div>
              )}
              <div style={{ marginTop: 8, color: "#666" }}>
                Победитель забирает суммарный выигрыш оппонента(ов).
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}


